<template>
	<div class="call-filter">
		<button
			id="call-filter-toggle"
			class="call-filter-toggle btn btn-outline-dark"
			aria-haspopup="listbox"
			aria-label="Call filter"
			@click.prevent="onToggleClick"
		>
			<div class="btn-ripple"></div>
			<span v-if="selected > 0" class="btn-label">
				Filters ({{ selected }})
			</span>
			<span v-else class="btn-label">
				Filters
			</span>

			<font-awesome-icon
				class="btn-icon"
				:icon="['fal', show ? 'chevron-up' : 'chevron-down']"
			/>
		</button>
		<transition name="fade">
			<div v-if="show" class="call-filter-dropdown" :class="{ show }">
				<div
					id="call-filter-list"
					tabindex="-1"
					role="listbox"
					aria-label="Choose a call filter"
					aria-multiselectable="true"
					class="call-filter-dropdown-list"
					@click.stop
				>
					<div class="call-filter-toggle-all">
						<div class="select-all-label">
							<span class="cursor-pointer" @click="deselectAll"
								>Select None
							</span>
						</div>
					</div>

					<div class="call-filter-header">
						Filters
					</div>

					<div
						v-if="hasPagingEnabled"
						id="call-filter-item-urgent"
						class="call-filter-item"
						role="option"
					>
						<div class="custom-checkbox" @click.self="toggleSource">
							<input
								id="call-filter-pages"
								v-model="source"
								type="checkbox"
								value="page"
							/>

							<label for="call-filter-pages">
								<span class="checkmark">
									<CheckIcon />
								</span>
								<span class="label-text">Pages</span>
							</label>
						</div>
					</div>

					<div
						id="call-filter-item-urgent"
						class="call-filter-item"
						role="option"
					>
						<div class="custom-checkbox" @click.self="toggleUrgent">
							<input
								id="call-filter-urgent"
								v-model="urgent"
								type="checkbox"
								:value="true"
							/>

							<label for="call-filter-urgent">
								<span class="checkmark">
									<CheckIcon />
								</span>
								<span class="label-text">Urgent</span>
							</label>
						</div>
					</div>

					<div
						id="call-filter-item-non-urgent"
						class="call-filter-item"
						role="option"
					>
						<div
							class="custom-checkbox"
							@click.self="toggleNonUrgent"
						>
							<input
								id="call-filter-non-urgent"
								v-model="nonUrgent"
								type="checkbox"
								:value="true"
							/>

							<label for="call-filter-non-urgent">
								<span class="checkmark">
									<CheckIcon />
								</span>
								<span class="label-text">Non-Urgent</span>
							</label>
						</div>
					</div>

					<div
						id="call-filter-item-complete"
						class="call-filter-item"
						role="option"
					>
						<div
							class="custom-checkbox"
							@click.self="toggleFilter('complete')"
						>
							<input
								id="call-filter-complete"
								v-model="filters"
								type="checkbox"
								value="complete"
							/>

							<label for="call-filter-complete">
								<span class="checkmark">
									<CheckIcon />
								</span>
								<span class="label-text">Complete</span>
							</label>
						</div>
					</div>

					<div
						id="call-filter-item-fallback"
						class="call-filter-item"
						role="option"
					>
						<div
							class="custom-checkbox"
							@click.self="toggleFilter('fallback')"
						>
							<input
								id="call-filter-fallback"
								v-model="filters"
								type="checkbox"
								value="fallback"
							/>

							<label for="call-filter-fallback">
								<span class="checkmark">
									<CheckIcon />
								</span>
								<span class="label-text">Fallback</span>
							</label>
						</div>
					</div>

					<div
						id="call-filter-item-incomplete"
						class="call-filter-item"
						role="option"
					>
						<div
							class="custom-checkbox"
							@click.self="toggleFilter('incomplete')"
						>
							<input
								id="call-filter-incomplete"
								v-model="filters"
								type="checkbox"
								value="incomplete"
							/>

							<label for="call-filter-incomplete">
								<span class="checkmark">
									<CheckIcon />
								</span>
								<span class="label-text">New</span>
							</label>
						</div>
					</div>

					<div
						id="call-filter-item-noted"
						class="call-filter-item"
						role="option"
					>
						<div
							class="custom-checkbox"
							@click.self="toggleFilter('noted')"
						>
							<input
								id="call-filter-noted"
								v-model="filters"
								type="checkbox"
								value="noted"
							/>

							<label for="call-filter-noted">
								<span class="checkmark">
									<CheckIcon />
								</span>
								<span class="label-text">Notes</span>
							</label>
						</div>
					</div>

					<div
						id="call-filter-item-voicemail"
						class="call-filter-item"
						role="option"
					>
						<div
							class="custom-checkbox"
							@click.self="toggleFilter('rsm_message')"
						>
							<input
								id="call-filter-voicemail"
								v-model="filters"
								type="checkbox"
								value="rsm_message"
							/>

							<label for="call-filter-voicemail">
								<span class="checkmark">
									<CheckIcon />
								</span>
								<span class="label-text">Voicemail</span>
							</label>
						</div>
					</div>

					<div
						id="call-filter-item-dismiss"
						class="call-filter-item"
						role="option"
					>
						<div
							class="custom-checkbox"
							@click.self="toggleFilter('dismiss')"
						>
							<input
								id="call-filter-dismiss"
								v-model="filters"
								type="checkbox"
								value="dismiss"
							/>

							<label for="call-filter-dismiss">
								<span class="checkmark">
									<CheckIcon />
								</span>
								<span class="label-text">Dismiss</span>
							</label>
						</div>
					</div>

					<div
						v-show="callTypes.length > 0"
						class="call-filter-header"
					>
						Call Types
					</div>

					<div
						v-for="type in callTypes"
						:id="`call-type-item-${type.id}`"
						:key="type.id"
						class="call-filter-item"
						role="option"
					>
						<div
							class="custom-checkbox"
							@click.self="toggleCallType(type)"
						>
							<input
								:id="`call-type-${type.id}`"
								v-model="types"
								type="checkbox"
								:value="type"
							/>
							<label :for="`call-type-${type.id}`">
								<span class="checkmark">
									<CheckIcon />
								</span>
								<span class="label-text">{{ type.name }}</span>
							</label>
						</div>

						<span class="call-filter-item-badge">
							{{ type.total }}
						</span>
					</div>

					<div v-if="officeOpenInfo.officeHoursSet">
						<div class="call-filter-header">
							Office Hours Types
						</div>

						<div
							v-for="type in officeHoursTypes"
							:id="`office-hours-type-item-${type}`"
							:key="type"
							class="call-filter-item"
							role="option"
						>
							<div
								class="custom-checkbox"
								@click.self="toggleOfficeHoursType(type)"
							>
								<input
									:id="`office-hours-type-${type}`"
									v-model="selectedOfficeHoursTypes"
									type="checkbox"
									:value="type"
								/>
								<label :for="`office-hours-type-${type}`">
									<span class="checkmark">
										<CheckIcon />
									</span>
									<span class="label-text text-capitalize">{{
										type
									}}</span>
								</label>
							</div>
						</div>
					</div>

					<div class="call-filter-submit">
						<button
							type="button"
							:disabled="applyingFilter"
							@click.prevent.stop="onApplyFiltersClick"
						>
							<span class="btn-label m-2">Apply Filters</span>
							<font-awesome-icon
								v-if="applyingFilter"
								spin
								fixed-width
								size="1x"
								margin="0 0 0 10px"
								:icon="['far', 'spinner-third']"
							/>
						</button>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import CheckIcon from '@/components/Icons/CheckIcon.vue'
import { OFFICE_HOURS_TYPES } from '@/config/constants'
/**
 * The escape numeric key code.
 *
 * @type {Number}
 */
const ESCAPE_KEY_CODE = 27

/**
 * The total number of statuses + urgent/non-urgent filters.
 *
 * @type {Number}
 */
const TOTAL_FILTERS = 6

export default {
	components: { CheckIcon },
	/**
	 * The component's computed properties.
	 *
	 * @type {Object}
	 */
	computed: {
		/**
		 * Number of selected items on dropdown.
		 *
		 * @return {Number}
		 */
		selected() {
			let filters = 0

			if (this.urgent) {
				filters++
			}

			if (this.nonUrgent) {
				filters++
			}
			const filtersLength = this.filters ? this.filters.length : 0
			const typesLength = this.types ? this.types.length : 0
			const sourceLength = Array.isArray(this.source)
				? this.source.length
				: 0
			const officeHoursLength = Array.isArray(
				this.selectedOfficeHoursTypes
			)
				? this.selectedOfficeHoursTypes.length
				: 0
			return (
				filtersLength +
				typesLength +
				filters +
				sourceLength +
				officeHoursLength
			)
		},

		officeHoursTypes() {
			const excluded = this.officeOpenInfo.lunchHoursSet
				? ['other']
				: ['lunch', 'other']
			return OFFICE_HOURS_TYPES.filter(type => !excluded.includes(type))
		},

		/**
		 * Total number of options.
		 *
		 * @return {Number}
		 */
		totalOptions() {
			return this.callTypes.length + TOTAL_FILTERS
		},

		...mapGetters('calls', ['hasFilter']),
		...mapGetters('partners', ['hasPagingEnabled']),

		...mapState({
			activeFilters: state => state.calls.filters.statuses,
			activeTypes: state => state.calls.filters.types,
			activeUrgent: state => state.calls.filters.urgent,
			activeNonUrgent: state => state.calls.filters.nonUrgent,
			activeSource: state => state.calls.source,
			activeOfficeHoursTypes: state => state.calls.filters.officeHours,
			officeOpenInfo: state => state.settings.officeOpenInfo,
		}),
	},

	/**
	 * The component's local methods.
	 *
	 * @type {Object}
	 */
	methods: {
		/**
		 * Add the escape keydown event listeners.
		 *
		 * @return {void}
		 */
		addEventListeners() {
			document.addEventListener('click', this.closeDropdown)
			document.addEventListener('keydown', this.onEscapeKeyDown)
		},

		/**
		 * Handle a global click event outside of the dropdown toggler.
		 *
		 * @return {void}
		 */
		closeDropdown() {
			if (this.show) {
				this.show = false
			}

			setTimeout(() => this.removeEventListeners(), 0)
		},

		/**
		 * Remove all selected filters and types.
		 *
		 * @return {void}
		 */
		deselectAll() {
			this.types = []
			this.filters = []
			this.urgent = false
			this.nonUrgent = false
			this.source = []
			this.selectedOfficeHoursTypes = []
		},

		/**
		 * Get the all of the filters as a store payload object.
		 *
		 * @return {Object}
		 */
		getFiltersAsObject() {
			return {
				nonUrgent: this.nonUrgent,
				statuses: this.filters,
				types: this.types,
				urgent: this.urgent,
			}
		},

		/**
		 * Determine if the given keycode is the escape key.
		 *
		 * @param {Number} keyCode
		 * @return {Boolean}
		 */
		isEscapeKeyCode(keyCode) {
			return keyCode === ESCAPE_KEY_CODE
		},

		/**
		 * Apply the filters and load the calls.
		 *
		 * @return {void}
		 */
		async onApplyFiltersClick() {
			const payload = {
				fromFilters: true,
				callId: this.$route.params.sid,
			}
			await this.setFilters(this.getFiltersAsObject())
			await this.setSource(this.source)
			await this.setOfficeHoursTypes(this.selectedOfficeHoursTypes || [])
			this.applyingFilter = true
			if (this.hasPagingEnabled) {
				await this.getIncompletePagingCount()
			}
			await this.fetchCalls(payload).then(() => {
				this.closeDropdown()
				this.applyingFilter = false
			})
		},

		/**
		 * Handle the escape keydown event.
		 *
		 * @returns {void}
		 */
		onEscapeKeyDown(event) {
			if (this.show && this.isEscapeKeyCode(event.keyCode)) {
				this.show = false

				this.removeEventListeners()
			}
		},

		/**
		 * Handle the dropdown click event.
		 *
		 * @return {void}
		 */
		onToggleClick() {
			this.show = !this.show

			setTimeout(() => {
				return this.show
					? this.addEventListeners()
					: this.removeEventListeners()
			}, 0)
		},

		/**
		 * Toggle source (Pages) when clicking the custom-checkbox div.
		 *
		 * @return {void}
		 */
		toggleSource() {
			const hasPage = (this.source || []).includes('page')
			this.source = hasPage
				? (this.source || []).filter(s => s !== 'page')
				: [...(this.source || []), 'page']
		},

		/**
		 * Toggle office hours type when clicking the custom-checkbox div.
		 *
		 * @return {void}
		 */
		toggleOfficeHoursType(type) {
			const list = this.selectedOfficeHoursTypes || []
			const typeLower = (type || '').toLowerCase()
			const index = list.findIndex(
				t => (t || '').toLowerCase() === typeLower
			)
			if (index >= 0) {
				this.selectedOfficeHoursTypes = list.filter(
					t => (t || '').toLowerCase() !== typeLower
				)
			} else {
				this.selectedOfficeHoursTypes = [...list, type]
			}
		},

		/**
		 * Toggle urgent when clicking the custom-checkbox div.
		 *
		 * @return {void}
		 */
		toggleUrgent() {
			this.urgent = !this.urgent
		},

		/**
		 * Toggle non-urgent when clicking the custom-checkbox div.
		 *
		 * @return {void}
		 */
		toggleNonUrgent() {
			this.nonUrgent = !this.nonUrgent
		},

		/**
		 * Toggle a filter value (complete, fallback, incomplete, noted, rsm_message, dismiss) when clicking the custom-checkbox div.
		 *
		 * @param {string} value
		 * @return {void}
		 */
		toggleFilter(value) {
			const list = this.filters || []
			const index = list.indexOf(value)
			if (index >= 0) {
				this.filters = list.filter(f => f !== value)
			} else {
				this.filters = [...list, value]
			}
		},

		/**
		 * Toggle call type selection when clicking the custom-checkbox div.
		 *
		 * @param {Object} type
		 * @return {void}
		 */
		toggleCallType(type) {
			const index = this.types.findIndex(t => t.id === type.id)
			if (index >= 0) {
				this.types = this.types.filter(t => t.id !== type.id)
			} else {
				this.types = [...this.types, type]
			}
		},

		/**
		 * Remove the global event listeners.
		 *
		 * @return {void}
		 */
		removeEventListeners() {
			document.removeEventListener('keydown', this.onEscapeKeyDown)
			document.removeEventListener('click', this.closeDropdown)
		},

		...mapActions('calls', {
			fetchCalls: 'get',
			setFilters: 'setFilters',
			setSource: 'setSource',
			setOfficeHoursTypes: 'setOfficeHoursTypes',
		}),

		...mapActions('pages', {
			getIncompletePagingCount: 'getIncompletePagingCount',
		}),
	},

	/**
	 * The component's name used for debugging.
	 *
	 * @type {String}
	 */
	name: 'CallFilterDropdown',

	/**
	 * The component's inherited properties.
	 *
	 * @type {Object}
	 */
	props: {
		/**
		 * The partner's call types collection.
		 *
		 * @type {Array}
		 */
		callTypes: {
			type: Array,
			default() {
				return []
			},
		},
	},

	/**
	 * The component's property watchers.
	 *
	 * @type {Object}
	 */
	watch: {
		/**
		 * Sync the local selected filters with the store's filter state.
		 *
		 * @return {void}
		 */

		activeFilters(newVal) {
			this.filters = newVal || []
		},

		/**
		 * Sync the local non-urgent checbox with the store's non-urgent filter state.
		 *
		 * @return {void}
		 */
		activeNonUrgent(newVal) {
			this.nonUrgent = newVal || false
		},

		/**
		 * Sync the local types with the store's active type filters.
		 *
		 * @return {void}
		 */
		activeTypes(newVal) {
			this.types = newVal || []
		},

		/**
		 * Sync the local urgent checkbox with the store's urgent filter state.
		 *
		 * @return {void}
		 */
		activeUrgent(newVal) {
			this.urgent = newVal || false
		},

		/**
		 * Sync the local source checkbox with the store's source filter state.
		 *
		 * @return {void}
		 */
		activeSource(newVal) {
			this.source = newVal || []
		},

		/**
		 * Sync the local office hours types with the store's office hours filter state.
		 *
		 * @return {void}
		 */
		activeOfficeHoursTypes(newVal) {
			this.selectedOfficeHoursTypes = newVal || []
		},

		/**
		 * Sync the local types & filters with the store when toggled.
		 *
		 * @return {void}
		 */
		show() {
			this.types = this.activeTypes || []
			this.filters = this.activeFilters || []
			this.urgent = this.activeUrgent || false
			this.nonUrgent = this.activeNonUrgent || false
			this.source = this.activeSource || []
			this.selectedOfficeHoursTypes = this.activeOfficeHoursTypes || []
		},
	},

	/**
	 * The component's before destroy lifecycle hook.
	 *
	 * @return {void}
	 */
	beforeDestroy() {
		this.removeEventListeners()
	},

	mounted() {
		this.types = this.activeTypes || []
		this.filters = this.activeFilters || []
		this.urgent = this.activeUrgent || false
		this.nonUrgent = this.activeNonUrgent || false
		this.source = this.activeSource || []
		this.selectedOfficeHoursTypes = this.activeOfficeHoursTypes || []
	},

	/**
	 * Get the component's initial state.
	 *
	 * @return {Object}
	 */
	data() {
		return {
			filters: this.activeFilters || [],
			nonUrgent: this.activeNonUrgent || false,
			show: false,
			types: this.activeTypes || [],
			urgent: this.activeUrgent || false,
			applyingFilter: false,
			source: this.activeSource || [],
			selectedOfficeHoursTypes: this.activeOfficeHoursTypes || [],
		}
	},
}
</script>
